import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { RotateCcw, Settings as SettingsIcon } from 'lucide-react'

import {
  loadDispatchMapData,
  type DispatchInspector,
  type DispatchMapData,
  type DispatchOrder,
  isOrderActive,
} from '@/api/dispatch_map'
import { listSystemConfig } from '@/api/system_config'
import {
  AssignInspectorError,
  assignInspectorToOrder,
} from '@/api/nearest_inspectors'
import { useInspectorsPositionsRealtime } from '@/hooks/useInspectorsPositionsRealtime'
import { loadYandexMaps, yandexMapsLoadFailed } from '@/lib/yandexMapsLoader'
import { YANDEX_MAPS_API_KEY } from '@/config'
import { mapPinDataUrl } from '@/features/map/mapPinIcon'
import {
  distanceLabelDataUrl,
  haversineKm,
  inspectorMarkerDataUrl,
  inspectorTone,
  type InspectorMarkerTone,
} from '@/features/dispatch-map/inspectorIcon'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PillToggle } from '@/components/ui/PillToggle'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { SearchInput } from '@/components/ui/SearchInput'
import { Popover } from '@/components/ui/Popover'
import { useAuthStore } from '@/store/authStore'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'
import i18n from '@/utils/i18n'
import { dateFnsLocale } from '@/utils/dateLocale'

/* eslint-disable @typescript-eslint/no-explicit-any */

const MOSCOW_CENTER: [number, number] = [55.751244, 37.618423]
const MOSCOW_ZOOM = 10

const COLOR_SEARCH = '#f7931a'   // оранжевый
const COLOR_ACTIVE = '#3b82f6'   // синий

type InspectorFilter = 'all' | 'free' | 'busy'

interface PendingAssign {
  order: DispatchOrder
  inspector: DispatchInspector
}

/**
 * /admin/dispatch-map — оперативная карта диспетчера.
 *
 * Маркеры:
 *  * Заказы: SEARCH — оранжевый pin, активные — синий. Терминальные не рисуем
 *    (не приходят из API).
 *  * Инспекторы: зелёный (свободен, свежая позиция) / жёлтый (свободен,
 *    устаревшая) / серый (занят активным заказом). Без позиции — не рисуем.
 *
 * Линии:
 *  * Постоянные: серые между занятым инспектором и его заказом, подпись —
 *    расстояние по прямой (гаверсинус).
 *  * Временные: оранжевые штриховые от выбранного SEARCH-заказа до всех
 *    свободных инспекторов в радиусе `dispatch_search_radius_km`. Появляются
 *    по клику на оранжевый маркер, исчезают на снятие выбора.
 *
 * Назначение: клик SEARCH → выбран; клик инспектора → ConfirmDialog →
 * `assignInspectorToOrder` → realtime обновит карту.
 */
export function DispatchMapPage() {
  const { t } = useTranslation()
  const profile = useAuthStore((s) => s.profile)
  const canAssign = profile?.role === 'admin' || profile?.role === 'dispatcher'
  const qc = useQueryClient()

  // ── Конфиг (дефолты из system_config) + локальные оверрайды ──────────────
  const { data: cfg } = useQuery({
    queryKey: ['system-config'],
    queryFn: listSystemConfig,
    staleTime: 60_000,
  })
  const defaultRadiusKm = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'dispatch_search_radius_km')?.value
    const n = v ? Number(v) : 50
    return Number.isFinite(n) && n > 0 ? Math.min(500, Math.max(5, Math.round(n))) : 50
  }, [cfg])
  const defaultFreshMinutes = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'position_fresh_minutes')?.value
    const n = v ? Number(v) : 5
    return Number.isFinite(n) && n > 0 ? Math.min(240, Math.max(1, Math.round(n))) : 5
  }, [cfg])

  // Локальные оверрайды (не пишутся в system_config). null → берём дефолт.
  const [radiusOverride, setRadiusOverride] = useState<number | null>(null)
  const [freshOverride, setFreshOverride] = useState<number | null>(null)
  const dispatchRadiusKm = radiusOverride ?? defaultRadiusKm
  const freshMinutes = freshOverride ?? defaultFreshMinutes

  // ── Данные ────────────────────────────────────────────────────────────────
  useInspectorsPositionsRealtime()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dispatch-map'],
    queryFn: loadDispatchMapData,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  })

  // Realtime на orders — отдельный канал на эту страницу.
  useEffect(() => {
    if (USE_MOCK) return
    const supabase = createClient()
    let cleanup: (() => void) | null = null
    let cancelled = false
    void (async () => {
      const { data: sess } = await supabase.auth.getSession()
      if (cancelled) return
      if (sess.session) supabase.realtime.setAuth(sess.session.access_token)
      const ch = supabase
        .channel('dispatch-map-orders')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'orders' },
          () => {
            void qc.invalidateQueries({ queryKey: ['dispatch-map'] })
          },
        )
        .subscribe()
      cleanup = () => {
        void supabase.removeChannel(ch)
      }
    })()
    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [qc])

  // ── Фильтры/поиск ────────────────────────────────────────────────────────
  const [showSearch, setShowSearch] = useState(true)
  const [showActive, setShowActive] = useState(true)
  const [inspectorFilter, setInspectorFilter] = useState<InspectorFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')

  function resetFilters() {
    setShowSearch(true)
    setShowActive(true)
    setInspectorFilter('all')
    setSearchQuery('')
    setRadiusOverride(null)
    setFreshOverride(null)
  }

  const isAllDefault =
    showSearch &&
    showActive &&
    inspectorFilter === 'all' &&
    searchQuery === '' &&
    radiusOverride == null &&
    freshOverride == null
  const hasParamOverride = radiusOverride != null || freshOverride != null

  // Счётчики только для тогглов «Поиск/В работе» (рядом с инспекторскими
  // фильтрами счётчики не показываем — слишком много цифр в одну строку).
  const counts = useMemo(() => {
    const orders = data?.orders ?? []
    let searchOrders = 0
    let activeOrders = 0
    for (const o of orders) {
      if (o.status === 'SEARCH') searchOrders++
      else if (isOrderActive(o.status)) activeOrders++
    }
    return { searchOrders, activeOrders }
  }, [data?.orders])

  const filteredOrders = useMemo(() => {
    const list = data?.orders ?? []
    return list.filter((o) => {
      if (o.status === 'SEARCH') return showSearch
      if (isOrderActive(o.status)) return showActive
      return false
    })
  }, [data?.orders, showSearch, showActive])

  const filteredInspectors = useMemo(() => {
    const list = data?.inspectors ?? []
    return list.filter((p) => {
      const busy = p.active_order != null
      if (inspectorFilter === 'free' && busy) return false
      if (inspectorFilter === 'busy' && !busy) return false
      return true
    })
  }, [data?.inspectors, inspectorFilter])

  // ── Карта ────────────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const orderPmRef = useRef(new Map<string, any>())
  const inspectorPmRef = useRef(new Map<string, any>())
  const permLineLayerRef = useRef<any[]>([])
  const tempLineLayerRef = useRef<any[]>([])
  const [mapReady, setMapReady] = useState(false)
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingAssign | null>(null)
  const [snack, setSnack] = useState<string | null>(null)

  // ── Фокус-режим: при выбранном SEARCH-заказе на карте остаются только он
  // сам + свободные инспекторы (потенциальные кандидаты). Остальные маркеры
  // и постоянные линии (busy↔order) скрыты, чтобы не было «месива».
  const isFocusMode = selectedOrderId != null

  const displayedOrders = useMemo(() => {
    if (!isFocusMode) return filteredOrders
    return filteredOrders.filter((o) => o.id === selectedOrderId)
  }, [filteredOrders, isFocusMode, selectedOrderId])

  const displayedInspectors = useMemo(() => {
    if (!isFocusMode) return filteredInspectors
    return filteredInspectors.filter((p) => p.active_order == null)
  }, [filteredInspectors, isFocusMode])

  // Bootstrap карты — один раз.
  useLayoutEffect(() => {
    if (!YANDEX_MAPS_API_KEY) return
    const el = containerRef.current
    if (!el) return
    let cancelled = false

    void (async () => {
      try {
        await loadYandexMaps()
      } catch (e) {
        if (!cancelled)
          setMapLoadError(
            e instanceof Error ? e.message : yandexMapsLoadFailed(),
          )
        return
      }
      const ymaps = (window as any).ymaps
      if (cancelled || !ymaps || mapRef.current) return

      const map = new ymaps.Map(
        el,
        {
          center: MOSCOW_CENTER,
          zoom: MOSCOW_ZOOM,
          controls: ['zoomControl', 'fullscreenControl'],
        },
        { suppressMapOpenBlock: true },
      )
      // Клик по пустому месту — снимаем выбор.
      map.events.add('click', () => {
        setSelectedOrderId(null)
      })
      mapRef.current = map
      setMapReady(true)
    })()

    return () => {
      cancelled = true
      try {
        mapRef.current?.destroy()
      } catch {
        /* empty */
      }
      mapRef.current = null
      orderPmRef.current.clear()
      inspectorPmRef.current.clear()
      permLineLayerRef.current = []
      tempLineLayerRef.current = []
    }
  }, [])

  // Перерисовка маркеров при изменении данных/фильтров.
  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    const ymaps = (window as any).ymaps
    if (!map || !ymaps) return

    const visibleOrderIds = new Set(displayedOrders.map((o) => o.id))
    const visibleInspectorIds = new Set(displayedInspectors.map((p) => p.id))

    // Удаляем «отфильтрованные» маркеры.
    for (const [id, pm] of Array.from(orderPmRef.current)) {
      if (!visibleOrderIds.has(id)) {
        try { map.geoObjects.remove(pm) } catch { /* empty */ }
        orderPmRef.current.delete(id)
      }
    }
    for (const [id, pm] of Array.from(inspectorPmRef.current)) {
      if (!visibleInspectorIds.has(id)) {
        try { map.geoObjects.remove(pm) } catch { /* empty */ }
        inspectorPmRef.current.delete(id)
      }
    }

    // Создаём/обновляем заказы.
    for (const o of displayedOrders) {
      const color = o.status === 'SEARCH' ? COLOR_SEARCH : COLOR_ACTIVE
      const href = mapPinDataUrl(color)
      let pm = orderPmRef.current.get(o.id)
      const hint = orderHintHtml(o)
      if (!pm) {
        pm = new ymaps.Placemark(
          [o.lat, o.lon],
          { hintContent: hint },
          {
            iconLayout: 'default#image',
            iconImageHref: href,
            iconImageSize: [32, 40],
            iconImageOffset: [-16, -40],
            // Балунов не используем (по UX-решению), отключаем стандартный.
            hasBalloon: false,
            openBalloonOnClick: false,
          },
        )
        pm.events.add('click', (e: any) => {
          e.preventDefault()
          e.stopPropagation()
          // Только SEARCH-заказы можно «выбрать» для назначения.
          // Активные — клик ничего не делает (карточку открыть можно правым
          // кликом или через сайдбар; средний клик не оверлоадим).
          if (o.status !== 'SEARCH') return
          setSelectedOrderId((prev) => (prev === o.id ? null : o.id))
        })
        map.geoObjects.add(pm)
        orderPmRef.current.set(o.id, pm)
      } else {
        pm.geometry.setCoordinates([o.lat, o.lon])
        pm.options.set('iconImageHref', href)
        pm.properties.set('hintContent', hint)
      }
    }

    // Создаём/обновляем инспекторов.
    for (const p of displayedInspectors) {
      const busy = p.active_order != null
      const ageMin = (Date.now() - new Date(p.last_position_at).getTime()) / 60_000
      const fresh = ageMin <= freshMinutes
      const tone: InspectorMarkerTone = inspectorTone(busy, fresh)
      const href = inspectorMarkerDataUrl(p.full_name, tone)
      let pm = inspectorPmRef.current.get(p.id)
      const hint = inspectorHintHtml(p, fresh)
      if (!pm) {
        pm = new ymaps.Placemark(
          [p.last_lat, p.last_lon],
          { hintContent: hint },
          {
            iconLayout: 'default#image',
            iconImageHref: href,
            iconImageSize: [32, 32],
            iconImageOffset: [-16, -16],
            hasBalloon: false,
            openBalloonOnClick: false,
          },
        )
        pm.events.add('click', (e: any) => {
          e.preventDefault()
          e.stopPropagation()
          // Если есть выбранный SEARCH-заказ — открываем ConfirmDialog.
          // Занятого инспектора тоже можно назначить (машина встанет в
          // очередь как ASSIGNED) — занятость лишь предупреждение в диалоге.
          const selOrderId = selectedOrderIdRef.current
          if (!selOrderId || !canAssign) return
          const order = (data?.orders ?? []).find((x) => x.id === selOrderId)
          if (!order || order.status !== 'SEARCH') return
          setPending({ order, inspector: p })
        })
        map.geoObjects.add(pm)
        inspectorPmRef.current.set(p.id, pm)
      } else {
        pm.geometry.setCoordinates([p.last_lat, p.last_lon])
        pm.options.set('iconImageHref', href)
        pm.properties.set('hintContent', hint)
      }
    }
  }, [mapReady, displayedOrders, displayedInspectors, freshMinutes, data, canAssign])

  // selectedOrderId доступен из event-listener — храним в ref, чтобы не терять стейт.
  const selectedOrderIdRef = useRef<string | null>(null)
  useEffect(() => {
    selectedOrderIdRef.current = selectedOrderId
  }, [selectedOrderId])

  // Если SEARCH-заказы скрыли — сбрасываем «выбранный» (он сам с карты убрался).
  useEffect(() => {
    if (!showSearch && selectedOrderId) setSelectedOrderId(null)
  }, [showSearch, selectedOrderId])

  // Постоянные линии (busy inspector ↔ order) + подписи.
  // Рисуем только если активные заказы вообще показываются (showActive),
  // и инспектор/заказ не отфильтрован.
  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    const ymaps = (window as any).ymaps
    if (!map || !ymaps) return

    for (const obj of permLineLayerRef.current) {
      try { map.geoObjects.remove(obj) } catch { /* empty */ }
    }
    permLineLayerRef.current = []

    if (!data || !showActive || isFocusMode) return

    const visibleOrderIds = new Set(filteredOrders.map((o) => o.id))
    const orderById = new Map(data.orders.map((o) => [o.id, o]))
    for (const p of filteredInspectors) {
      if (!p.active_order) continue
      if (!visibleOrderIds.has(p.active_order.id)) continue
      const o = orderById.get(p.active_order.id)
      if (!o) continue
      addLine(
        ymaps,
        map,
        permLineLayerRef.current,
        [p.last_lat, p.last_lon],
        [o.lat, o.lon],
        { strokeColor: '#94a3b8', strokeWidth: 2, dashed: false },
      )
    }
  }, [mapReady, data, filteredInspectors, filteredOrders, showActive, isFocusMode])

  // Временные линии (selected SEARCH ↔ free inspectors в радиусе).
  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    const ymaps = (window as any).ymaps
    if (!map || !ymaps) return

    for (const obj of tempLineLayerRef.current) {
      try { map.geoObjects.remove(obj) } catch { /* empty */ }
    }
    tempLineLayerRef.current = []

    if (!selectedOrderId || !data || !showSearch) return
    const order = data.orders.find((o) => o.id === selectedOrderId)
    if (!order || order.status !== 'SEARCH') return

    for (const p of data.inspectors) {
      if (p.active_order != null) continue
      const km = haversineKm(
        { lat: order.lat, lon: order.lon },
        { lat: p.last_lat, lon: p.last_lon },
      )
      if (km > dispatchRadiusKm) continue
      addLine(
        ymaps,
        map,
        tempLineLayerRef.current,
        [p.last_lat, p.last_lon],
        [order.lat, order.lon],
        { strokeColor: '#f7931a', strokeWidth: 2, dashed: true },
      )
    }
  }, [mapReady, data, selectedOrderId, dispatchRadiusKm, showSearch])

  // Debounced поиск: 300мс после ввода → автоматически центрируем карту.
  // Enter (см. ниже onKeyDown) даёт немедленный результат.
  useEffect(() => {
    if (!searchQuery.trim()) return
    const t = window.setTimeout(() => {
      performSearch()
    }, 300)
    return () => window.clearTimeout(t)
    // performSearch стабилен по поведению: читает свежий searchQuery/data из замыкания.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Поиск: центрируем карту на найденном объекте.
  function performSearch() {
    const q = searchQuery.trim().toLowerCase()
    if (!q || !data || !mapRef.current) return
    const order =
      data.orders.find(
        (o) => o.id.toLowerCase().includes(q) ||
               (o.brand + ' ' + o.model).toLowerCase().includes(q),
      ) ?? null
    if (order) {
      mapRef.current.setCenter([order.lat, order.lon], 14, { duration: 300 })
      const pm = orderPmRef.current.get(order.id)
      pm?.balloon.open()
      return
    }
    const insp = data.inspectors.find(
      (p) => (p.full_name ?? '').toLowerCase().includes(q),
    )
    if (insp) {
      mapRef.current.setCenter([insp.last_lat, insp.last_lon], 14, { duration: 300 })
      const pm = inspectorPmRef.current.get(insp.id)
      pm?.balloon.open()
    }
  }

  // ── Назначение ───────────────────────────────────────────────────────────
  const assignMut = useMutation({
    mutationFn: ({ orderId, inspectorId }: { orderId: string; inspectorId: string }) =>
      assignInspectorToOrder(orderId, inspectorId),
    onSuccess: (_res, vars) => {
      const name = pending?.inspector.full_name ?? t('geo.map.inspectorFallback')
      setSnack(t('geo.map.assignSnack', { name }))
      // Оптимистично патчим кеш карты: order → ASSIGNED + inspector_id;
      // inspector.active_order = {id, ASSIGNED}. Чтобы серая сплошная линия
      // и «занят»-тон появились сразу, не дожидаясь refetch/realtime.
      qc.setQueryData<DispatchMapData | undefined>(['dispatch-map'], (prev) => {
        if (!prev) return prev
        return {
          orders: prev.orders.map((o) =>
            o.id === vars.orderId
              ? { ...o, inspector_id: vars.inspectorId, status: 'ASSIGNED' }
              : o,
          ),
          inspectors: prev.inspectors.map((p) =>
            p.id === vars.inspectorId
              ? { ...p, active_order: { id: vars.orderId, status: 'ASSIGNED' } }
              : p,
          ),
        }
      })
      setPending(null)
      setSelectedOrderId(null)
      void refetch()
      void qc.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (e: Error) => {
      const msg =
        e instanceof AssignInspectorError
          ? translateAssignError(e.code)
          : e.message
      setSnack(t('geo.positions.errorPrefix', { message: msg }))
      setPending(null)
      void refetch()
    },
  })

  // ── Render ───────────────────────────────────────────────────────────────
  if (!YANDEX_MAPS_API_KEY) {
    return <ErrorBanner text={t('geo.map.noApiKey')} />
  }

  return (
    <div className="flex h-[calc(100dvh-var(--stryzh-layout-header-height,80px))] flex-col">
      <PageHeader title={t('geo.map.title')} />

      {/* Toolbar: фильтры заказов │ фильтр инспекторов │ поиск + параметры + сброс. Одна строка. */}
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl lg:flex-nowrap border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-3 py-2">
        {/* Группа: заказы */}
        <div className="flex shrink-0 items-center gap-3">
          <PillToggle
            checked={showSearch}
            onChange={setShowSearch}
            label={t('geo.map.toggleSearch')}
            count={counts.searchOrders}
            className="!text-base"
          />
          <PillToggle
            checked={showActive}
            onChange={setShowActive}
            label={t('geo.map.toggleActive')}
            count={counts.activeOrders}
            className="!text-base"
          />
        </div>

        <span className="hidden h-6 w-px shrink-0 bg-[color:var(--stryzh-color-border-default)] lg:block" />

        {/* Группа: инспекторы */}
        <SegmentedControl<InspectorFilter>
          value={inspectorFilter}
          onChange={setInspectorFilter}
          options={[
            { value: 'all',  label: t('geo.map.inspectorsAll') },
            { value: 'free', label: t('geo.map.inspectorsFree') },
            { value: 'busy', label: t('geo.map.inspectorsBusy') },
          ]}
          aria-label={t('geo.map.inspectorsAria')}
          className="!w-auto shrink-0 lg:min-w-[320px] [&>button]:!flex-none lg:[&>button]:!flex-1"
        />

        <span className="hidden h-6 w-px shrink-0 bg-[color:var(--stryzh-color-border-default)] lg:block" />

        {/* Поиск растягивается; параметры и сброс в конце */}
        <div className="flex min-w-0 grow basis-full items-center gap-2 lg:basis-0">
          <SearchInput
            className="!max-w-none w-full"
            placeholder={t('geo.map.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') performSearch()
            }}
          />

          <Popover
            ariaLabel={t('geo.map.vizParamsAria')}
            trigger={
              <>
                <SettingsIcon size={16} aria-hidden />
                <span className="sr-only">{t('geo.map.vizParams')}</span>
                {hasParamOverride ? (
                  <span
                    aria-hidden
                    className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                  />
                ) : null}
              </>
            }
          >
            <div className="text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
              {t('geo.map.vizParamsHeading')}
            </div>
            <NumberStepper
              label={t('geo.map.radiusKm')}
              value={dispatchRadiusKm}
              min={5}
              max={500}
              step={5}
              onChange={(v) => setRadiusOverride(v)}
              isDefault={radiusOverride == null}
            />
            <div className="text-[11px] leading-tight text-[color:var(--stryzh-color-text-secondary)] opacity-70">
              {t('geo.map.radiusHint')}
            </div>
            <NumberStepper
              label={t('geo.map.freshMin')}
              value={freshMinutes}
              min={1}
              max={240}
              step={1}
              onChange={(v) => setFreshOverride(v)}
              isDefault={freshOverride == null}
            />
            <div className="text-[11px] leading-tight text-[color:var(--stryzh-color-text-secondary)] opacity-70">
              {t('geo.map.freshHint')}
            </div>
            {hasParamOverride ? (
              <button
                type="button"
                onClick={() => {
                  setRadiusOverride(null)
                  setFreshOverride(null)
                }}
                className="self-start rounded border border-[color:var(--stryzh-color-border-default)] bg-transparent px-2 py-1 text-xs text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
              >
                {t('geo.map.toDefaults')}
              </button>
            ) : null}
          </Popover>

          <button
            type="button"
            onClick={resetFilters}
            disabled={isAllDefault}
            aria-label={t('geo.map.resetFilters')}
            title={t('geo.map.reset')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--stryzh-color-border-outline)] bg-[color:var(--stryzh-color-fill-input)] text-[color:var(--stryzh-color-text-secondary)] transition-colors hover:enabled:text-[color:var(--stryzh-color-text-primary)] hover:enabled:border-[color:var(--stryzh-color-border-default)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw size={16} aria-hidden />
          </button>
        </div>
      </div>

      {error ? (
        <ErrorBanner
          text={t('geo.positions.errorPrefix', { message: (error as Error).message })}
        />
      ) : null}
      {mapLoadError ? <ErrorBanner text={mapLoadError} /> : null}
      {isLoading && !data ? <Loader /> : null}

      <div
        ref={containerRef}
        className="dispatch-map-bw relative flex-1 overflow-hidden rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]"
      />

      {selectedOrderId ? (
        <div className="mt-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('geo.map.selectedHint')}
        </div>
      ) : null}

      <ConfirmDialog
        open={pending != null}
        title={t('geo.map.assignTitle')}
        confirmLabel={assignMut.isPending ? t('geo.map.assigning') : t('geo.map.assign')}
        cancelLabel={t('common.cancel')}
        onCancel={() => (assignMut.isPending ? null : setPending(null))}
        onConfirm={() => {
          if (pending && !assignMut.isPending) {
            assignMut.mutate({
              orderId: pending.order.id,
              inspectorId: pending.inspector.id,
            })
          }
        }}
      >
        {pending ? (
          <div className="space-y-2 text-sm text-[color:var(--stryzh-color-text-primary)]">
            <p>
              {t('geo.map.assignBodyPre')}{' '}
              <b>{pending.inspector.full_name ?? t('geo.positions.noNamePlaceholder')}</b>{' '}
              {t('geo.map.assignBodyOrder')}{' '}
              <b>{pending.order.brand} {pending.order.model}</b>{' '}
              <code className="font-mono text-xs">{pending.order.id.slice(0, 8)}…</code>?
              <br />
              {t('geo.map.assignBodyTail')}
            </p>
            {pending.inspector.active_order != null ? (
              <p className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-100">
                {t('geo.map.assignBusyWarning')}
              </p>
            ) : null}
          </div>
        ) : null}
      </ConfirmDialog>

      {snack ? (
        <div className="fixed bottom-4 right-4 z-[70] rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] shadow-lg">
          {snack}
          <button
            type="button"
            onClick={() => setSnack(null)}
            className="ml-3 text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
          >×</button>
        </div>
      ) : null}
    </div>
  )
}

// ── helpers ─────────────────────────────────────────────────────────────────

function NumberStepper({
  label,
  value,
  min,
  max,
  step,
  onChange,
  isDefault,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
  isDefault: boolean
}) {
  return (
    <label
      className="flex items-center gap-1.5 text-xs text-[color:var(--stryzh-color-text-secondary)]"
      title={isDefault ? i18n.t('geo.map.paramDefaultTitle') : i18n.t('geo.map.paramOverrideTitle')}
    >
      <span className="opacity-80">{label}:</span>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (!Number.isFinite(n)) return
          onChange(Math.min(max, Math.max(min, Math.round(n))))
        }}
        className={`w-16 rounded border bg-[color:var(--stryzh-color-void)] px-2 py-1 font-mono text-xs text-[color:var(--stryzh-color-text-primary)] ${
          isDefault
            ? 'border-[color:var(--stryzh-color-border-default)]'
            : 'border-[color:var(--stryzh-color-accent-orange)]/60'
        }`}
      />
    </label>
  )
}

function addLine(
  ymaps: any,
  map: any,
  layer: any[],
  from: [number, number],
  to: [number, number],
  opts: { strokeColor: string; strokeWidth: number; dashed: boolean },
) {
  const line = new ymaps.Polyline(
    [from, to],
    {},
    {
      strokeColor: opts.strokeColor,
      strokeWidth: opts.strokeWidth,
      strokeStyle: opts.dashed ? 'shortdash' : 'solid',
      // линии — декорация, не интерактивны
      interactivityModel: 'default#transparent',
    },
  )
  map.geoObjects.add(line)
  layer.push(line)

  // Подпись по midpoint.
  const km = haversineKm(
    { lat: from[0], lon: from[1] },
    { lat: to[0], lon: to[1] },
  )
  const mid: [number, number] = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2]
  const labelHref = distanceLabelDataUrl(km)
  const label = new ymaps.Placemark(
    mid,
    {},
    {
      iconLayout: 'default#image',
      iconImageHref: labelHref,
      iconImageSize: [Math.max(48, 8 + i18n.t('geo.map.distanceKm', { km: km.toFixed(1) }).length * 7), 20],
      iconImageOffset: [-Math.max(48, 8 + i18n.t('geo.map.distanceKm', { km: km.toFixed(1) }).length * 7) / 2, -10],
      interactivityModel: 'default#transparent',
    },
  )
  map.geoObjects.add(label)
  layer.push(label)
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Hint при наведении на маркер заказа — компактная карточка без действий.
 * `hintContent` ymaps умеет принимать HTML-строку.
 */
function orderHintHtml(o: DispatchOrder): string {
  const title = escapeHtml(`${o.brand} ${o.model}`.trim())
  const price =
    o.final_price != null
      ? `${o.final_price.toLocaleString('ru-RU')} ₽`
      : o.desired_price != null
      ? `${o.desired_price.toLocaleString('ru-RU')} ₽`
      : '—'
  const status = escapeHtml(o.status)
  const created = escapeHtml(
    formatDistanceToNow(new Date(o.created_at), { locale: dateFnsLocale(), addSuffix: true }),
  )
  return `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px;line-height:1.35;padding:2px 0;max-width:220px">
  <div style="font-weight:600">${title}</div>
  <div style="opacity:0.75;font-size:11px">${status} · ${created}</div>
  <div style="margin-top:2px">${escapeHtml(price)}</div>
  <div style="opacity:0.85;margin-top:2px">${escapeHtml(o.address)}</div>
</div>`
}

function inspectorHintHtml(p: DispatchInspector, isFresh: boolean): string {
  const name = escapeHtml(p.full_name ?? i18n.t('geo.positions.noNamePlaceholder'))
  const last = escapeHtml(
    formatDistanceToNow(new Date(p.last_position_at), { locale: dateFnsLocale(), addSuffix: true }),
  )
  const status = escapeHtml(
    p.active_order
      ? i18n.t('geo.map.hint.busy', { status: p.active_order.status })
      : isFresh
        ? i18n.t('geo.map.hint.available')
        : i18n.t('geo.map.hint.availableStale'),
  )
  return `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px;line-height:1.35;padding:2px 0;max-width:200px">
  <div style="font-weight:600">${name}</div>
  <div style="opacity:0.75;font-size:11px">${status}</div>
  <div style="opacity:0.85;margin-top:2px">${escapeHtml(i18n.t('geo.map.hint.position', { time: last }))}</div>
</div>`
}

function translateAssignError(code: string): string {
  switch (code) {
    case 'order_not_in_search':
      return i18n.t('geo.assignError.order_not_in_search')
    case 'inspector_already_busy':
      return i18n.t('geo.assignError.inspector_already_busy')
    case 'not_an_active_inspector':
      return i18n.t('geo.assignError.not_an_active_inspector')
    case 'inspector_not_found':
      return i18n.t('geo.assignError.inspector_not_found')
    case 'forbidden':
      return i18n.t('geo.assignError.forbidden')
    case 'order_has_no_coordinates':
      return i18n.t('geo.assignError.order_has_no_coordinates')
    default:
      return code
  }
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div className="mb-2 rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      {text}
    </div>
  )
}
